import React from 'react';

/**
 * Enterprise Color Palette component for design system documentation
 * This component displays the AWS enterprise color palette with accessibility information
 */
const EnterpriseColorPalette = () => {
  // Primary colors - AWS Professional Blues
  const primaryColors = [
    { name: 'primary-50', label: 'Lightest background', hex: '#EFF8FF' },
    { name: 'primary-100', label: 'Light background', hex: '#DBEAFE' },
    { name: 'primary-200', label: 'Borders, dividers', hex: '#BFDBFE' },
    { name: 'primary-300', label: 'Disabled text', hex: '#93C5FD' },
    { name: 'primary-400', label: 'Secondary text', hex: '#60A5FA' },
    { name: 'primary-500', label: 'Body text', hex: '#3B82F6' },
    { name: 'primary-600', label: 'Headings', hex: '#2563EB' },
    { name: 'primary-700', label: 'Strong headings', hex: '#1D4ED8' },
    { name: 'primary-800', label: 'Very dark text', hex: '#1E40AF' },
    { name: 'primary-900', label: 'Darkest text', hex: '#1E3A8A' },
  ];

  // Secondary colors - AWS Slate
  const secondaryColors = [
    { name: 'secondary-50', label: 'Lightest background', hex: '#F8FAFC' },
    { name: 'secondary-100', label: 'Light background', hex: '#F1F5F9' },
    { name: 'secondary-200', label: 'Borders, dividers', hex: '#E2E8F0' },
    { name: 'secondary-300', label: 'Disabled text', hex: '#CBD5E1' },
    { name: 'secondary-400', label: 'Secondary text', hex: '#94A3B8' },
    { name: 'secondary-500', label: 'Body text', hex: '#64748B' },
    { name: 'secondary-600', label: 'Headings', hex: '#475569' },
    { name: 'secondary-700', label: 'Strong headings', hex: '#334155' },
    { name: 'secondary-800', label: 'Very dark text', hex: '#1E293B' },
    { name: 'secondary-900', label: 'Darkest text', hex: '#0F172A' },
  ];

  // Accent colors - AWS Orange
  const accentColors = [
    { name: 'accent-50', label: 'Lightest', hex: '#FFF7ED' },
    { name: 'accent-100', label: 'Light', hex: '#FFEDD5' },
    { name: 'accent-200', label: 'Borders', hex: '#FED7AA' },
    { name: 'accent-300', label: 'Light elements', hex: '#FDBA74' },
    { name: 'accent-400', label: 'Default', hex: '#FB923C' },
    { name: 'accent-500', label: 'Primary accent', hex: '#F97316' },
    { name: 'accent-600', label: 'Hover', hex: '#EA580C' },
    { name: 'accent-700', label: 'Active/pressed', hex: '#C2410C' },
    { name: 'accent-800', label: 'Dark accent', hex: '#9A3412' },
    { name: 'accent-900', label: 'Darkest', hex: '#7C2D12' },
  ];

  // AWS Brand colors
  const awsBrandColors = [
    { name: 'aws-blue', label: 'AWS Dark Blue (Squid Ink)', hex: '#232F3E' },
    { name: 'aws-orange', label: 'AWS Orange (Smile)', hex: '#FF9900' },
    { name: 'aws-anchor', label: 'AWS Anchor Blue', hex: '#0073BB' },
  ];

  // Feedback colors
  const feedbackColors = [
    { name: 'success-400', label: 'Success Default', hex: '#34D399' },
    { name: 'success-500', label: 'Success Primary', hex: '#10B981' },
    { name: 'warning-400', label: 'Warning Default', hex: '#FBBF24' },
    { name: 'warning-500', label: 'Warning Primary', hex: '#F59E0B' },
    { name: 'error-400', label: 'Error Default', hex: '#F87171' },
    { name: 'error-500', label: 'Error Primary', hex: '#EF4444' },
    { name: 'info-400', label: 'Info Default', hex: '#60A5FA' },
    { name: 'info-500', label: 'Info Primary', hex: '#3B82F6' },
  ];

  // Calculate contrast ratio
  const calculateContrastRatio = (color: string): number => {
    // Convert hex to RGB
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);

    // Calculate relative luminance
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

    // Calculate contrast with white (1.0) and black (0.0)
    const whiteContrast = (1.0 + 0.05) / (luminance + 0.05);
    const blackContrast = (luminance + 0.05) / (0.0 + 0.05);

    return Math.max(whiteContrast, blackContrast);
  };

  // Determine if a color passes WCAG AA for normal text (4.5:1)
  const passesWCAGAA = (color: string): boolean => {
    return calculateContrastRatio(color) >= 4.5;
  };

  // Determine if a color passes WCAG AAA for normal text (7:1)
  const passesWCAGAAA = (color: string): boolean => {
    return calculateContrastRatio(color) >= 7;
  };

  // Determine text color for best contrast
  const getTextColor = (bgColor: string): string => {
    // Convert hex to RGB
    const r = parseInt(bgColor.slice(1, 3), 16);
    const g = parseInt(bgColor.slice(3, 5), 16);
    const b = parseInt(bgColor.slice(5, 7), 16);

    // Calculate luminance (simplified)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.5 ? '#0F172A' : '#FFFFFF';
  };

  // Render a color swatch
  const renderColorSwatch = (color: { name: string; label: string; hex: string }) => {
    const textColor = getTextColor(color.hex);
    const contrastRatio = calculateContrastRatio(color.hex).toFixed(2);
    const wcagAA = passesWCAGAA(color.hex);
    const wcagAAA = passesWCAGAAA(color.hex);

    return (
      <div key={color.name} className="mb-2">
        <div
          className="h-16 rounded-t-md p-3 flex flex-col justify-between"
          style={{ backgroundColor: color.hex, color: textColor }}
        >
          <div className="font-medium">{color.name}</div>
          <div className="text-sm">{color.hex}</div>
        </div>
        <div className="bg-surface p-2 rounded-b-md border border-secondary-200 text-xs">
          <div className="flex justify-between">
            <span>{color.label}</span>
            <span>Contrast: {contrastRatio}</span>
          </div>
          <div className="flex space-x-2 mt-1">
            <span
              className={`px-1 rounded ${wcagAA ? 'bg-success-100 text-success-700' : 'bg-error-100 text-error-700'}`}
            >
              AA: {wcagAA ? 'Pass' : 'Fail'}
            </span>
            <span
              className={`px-1 rounded ${wcagAAA ? 'bg-success-100 text-success-700' : 'bg-error-100 text-error-700'}`}
            >
              AAA: {wcagAAA ? 'Pass' : 'Fail'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <h1 className="qnav-heading-1 mb-6">AWS Enterprise Color Palette</h1>

      <div className="mb-8">
        <h2 className="qnav-heading-2 mb-4">Enterprise Design Principles</h2>
        <p className="mb-4">
          Our color system is designed for AWS partners and technical advisors, reflecting trust, innovation, and
          professionalism. The palette follows a balanced approach with:
        </p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>
            <strong>Primary Colors (AWS Professional Blues):</strong> Used for primary actions, links, and key UI
            elements
          </li>
          <li>
            <strong>Secondary Colors (AWS Slate):</strong> Used for text, backgrounds, and UI structure
          </li>
          <li>
            <strong>Accent Colors (AWS Orange):</strong> Used for calls-to-action and important highlights
          </li>
          <li>
            <strong>AWS Brand Colors:</strong> Used for brand identity and recognition
          </li>
        </ul>
      </div>

      <div className="mb-8">
        <h2 className="qnav-heading-2 mb-4">Primary Colors - AWS Professional Blues</h2>
        <p className="mb-4">Used for primary actions, links, and key UI elements.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {primaryColors.map(renderColorSwatch)}
        </div>
      </div>

      <div className="mb-8">
        <h2 className="qnav-heading-2 mb-4">Secondary Colors - AWS Slate</h2>
        <p className="mb-4">Used for text, backgrounds, and UI structure.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {secondaryColors.map(renderColorSwatch)}
        </div>
      </div>

      <div className="mb-8">
        <h2 className="qnav-heading-2 mb-4">Accent Colors - AWS Orange</h2>
        <p className="mb-4">Used for calls-to-action and important highlights.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {accentColors.map(renderColorSwatch)}
        </div>
      </div>

      <div className="mb-8">
        <h2 className="qnav-heading-2 mb-4">AWS Brand Colors</h2>
        <p className="mb-4">Official AWS brand colors for recognition and consistency.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">{awsBrandColors.map(renderColorSwatch)}</div>
      </div>

      <div className="mb-8">
        <h2 className="qnav-heading-2 mb-4">Feedback Colors</h2>
        <p className="mb-4">Used for system feedback, alerts, and status indicators.</p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">{feedbackColors.map(renderColorSwatch)}</div>
      </div>

      <div className="mb-8">
        <h2 className="qnav-heading-2 mb-4">Enterprise UI Components</h2>

        <h3 className="qnav-heading-3 mt-6 mb-3">Button Hierarchy</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="p-4 bg-surface border border-secondary-200 rounded-md">
            <h4 className="font-medium mb-2">Primary Actions</h4>
            <button className="qnav-btn-primary w-full mb-2">Primary Button</button>
            <p className="text-xs text-secondary-500 mt-2">
              Used for the main action in a section or form. Only one primary button should appear in a section.
            </p>
          </div>

          <div className="p-4 bg-surface border border-secondary-200 rounded-md">
            <h4 className="font-medium mb-2">Secondary Actions</h4>
            <button className="qnav-btn-secondary w-full mb-2">Secondary Button</button>
            <p className="text-xs text-secondary-500 mt-2">
              Used for alternative actions that are less important than the primary action.
            </p>
          </div>

          <div className="p-4 bg-surface border border-secondary-200 rounded-md">
            <h4 className="font-medium mb-2">Tertiary Actions</h4>
            <button className="qnav-btn-outline w-full mb-2">Outline Button</button>
            <button className="qnav-btn-ghost w-full mb-2">Ghost Button</button>
            <p className="text-xs text-secondary-500 mt-2">
              Used for less important actions or in areas with multiple actions.
            </p>
          </div>
        </div>

        <h3 className="qnav-heading-3 mt-6 mb-3">AWS-Specific Components</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="qnav-aws-card">
            <div className="qnav-aws-card-header">AWS Card Header</div>
            <div className="qnav-aws-card-body">
              <p className="mb-4">AWS-styled card component for enterprise applications.</p>
              <div className="flex flex-wrap gap-2">
                <span className="qnav-aws-tag">Tag 1</span>
                <span className="qnav-aws-tag">Tag 2</span>
                <span className="qnav-aws-tag">Tag 3</span>
              </div>
            </div>
          </div>

          <div className="p-4 bg-surface border border-secondary-200 rounded-md">
            <h4 className="font-medium mb-2">AWS Button</h4>
            <button className="qnav-btn-aws w-full mb-2">AWS Button</button>
            <div className="mt-4">
              <h4 className="font-medium mb-2">Status Indicators</h4>
              <div className="flex flex-wrap gap-2">
                <span className="qnav-status-success">Success</span>
                <span className="qnav-status-warning">Warning</span>
                <span className="qnav-status-error">Error</span>
                <span className="qnav-status-info">Info</span>
                <span className="qnav-status-neutral">Neutral</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="qnav-heading-2 mb-4">Accessibility</h2>
        <p className="mb-4">
          All color combinations in our enterprise UI meet WCAG 2.1 AA standards for contrast (minimum 4.5:1 for normal
          text). Primary interactive elements and text meet AAA standards (minimum 7:1) where possible.
        </p>

        <div className="p-4 bg-surface border border-secondary-200 rounded-md">
          <h3 className="qnav-heading-3 mb-3">Enterprise Color Application</h3>
          <p className="mb-4">Our enterprise color application follows these principles:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Text Hierarchy:</strong> Darker shades for headings, medium shades for body text, lighter shades
              for secondary text
            </li>
            <li>
              <strong>Interactive Elements:</strong> Primary blue for main actions, slate for secondary actions, orange
              for attention-grabbing elements
            </li>
            <li>
              <strong>Backgrounds:</strong> Light neutral backgrounds with subtle contrast between sections
            </li>
            <li>
              <strong>Feedback:</strong> Consistent color coding for success, warning, error, and information states
            </li>
            <li>
              <strong>AWS Brand:</strong> Strategic use of AWS brand colors for recognition and trust
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseColorPalette;
